import { Box } from '@chakra-ui/react';

import { CourseLevel } from 'types';

import { SummaryRow } from '../../../Summary/SummaryRow';
import { Q } from '../../../../store/student/types';
import { IRisk, IStudentVisa } from '../../../../types';

import { getCourseLevel } from '../../../pages/history/qPage1/utils';
import { SummaryGroup } from '../../../Summary/SummaryGroup';
import { getVisaLabelFromValue, getSuccessfullyCompletedCourseLabelFromValue } from 'data';

interface Props {
  showRisks: boolean;

  lastCourse?: string;
  lastCourseOther?: string;
  lastCourseYear?: string;
  hasOverseasTravel?: string;
  overseasTravelTimes?: string;
  hasUkTravel?: string;
  ukTravelTimes?: string;
  hasPreviousUkStudyVisa?: string;
  hasPreviousUkStudy?: string;
  previousUkStudyVisas?: Array<IStudentVisa>;
  lastCourseLevel?: CourseLevel;
  lowerOrSameLevelReason?: string;
  riskList?: IRisk['historyRisk']['priorStudyAndTravel'];
}

function PriorStudyAndTravel({
  showRisks,
  lastCourse,
  lastCourseOther,
  lastCourseYear,
  hasOverseasTravel,
  overseasTravelTimes,
  hasUkTravel,
  ukTravelTimes,
  hasPreviousUkStudyVisa,
  hasPreviousUkStudy,
  previousUkStudyVisas,
  lastCourseLevel,
  lowerOrSameLevelReason,
  riskList = [],
}: Props) {
  return (
    <>
      <SummaryRow
        key="lastCourse"
        label="Last Course"
        fullLabel="What was the last course you successfully completed?"
        value={getSuccessfullyCompletedCourseLabelFromValue(lastCourse)}
        isRisk={showRisks && riskList.includes(Q.LAST_COURSE)}
      />

      {lastCourse === 'Other' && (
        <SummaryRow
          key="lastCourseOther"
          label="Name"
          fullLabel="Please state why you chose other:"
          value={lastCourseOther}
          isRisk={showRisks && riskList.includes(Q.LAST_COURSE_OTHER)}
        />
      )}

      <SummaryRow
        key="lastCourseYear"
        label="Year"
        fullLabel="In which year did you complete this course?"
        value={lastCourseYear}
        isRisk={showRisks && riskList.includes(Q.LAST_COURSE_YEAR)}
      />

      <SummaryRow
        key="overseasTravel"
        label="Overseas Travel"
        fullLabel="Have you ever travelled outside of your home country in the last 10 years?"
        value={hasOverseasTravel}
        isRisk={showRisks && riskList.includes(Q.HAS_OVERSEAS_TRAVEL)}
      />

      {hasOverseasTravel === 'Yes' && (
        <>
          <SummaryRow
            key="overseasTravelTimes"
            label="Travel Times"
            fullLabel="Approximately how many times?"
            value={overseasTravelTimes}
            isRisk={showRisks && riskList.includes(Q.OVERSEAS_TRAVEL_TIMES)}
          />

          <SummaryRow
            key="ukTravel"
            label="Travelled to the UK in the last 10 years"
            fullLabel="Have you ever travelled to the UK in the last 10 years?"
            value={hasUkTravel}
            isRisk={showRisks && riskList.includes(Q.HAS_UK_TRAVEL)}
          />

          {hasUkTravel === 'Yes' && (
            <SummaryRow
              key="ukTravelTimes"
              label="How many times"
              fullLabel="Approximately how many times?"
              value={ukTravelTimes}
              isRisk={showRisks && riskList.includes(Q.UK_TRAVEL_TIMES)}
            />
          )}
        </>
      )}

      <SummaryRow
        key="hasPreviousUkStudyVisa"
        label="Previous UK Study"
        fullLabel="Have you studied in the UK before?"
        value={hasPreviousUkStudy}
        isRisk={showRisks && riskList.includes(Q.HAS_PREVIOUS_UK_STUDY)}
      />

      <SummaryRow
        key="prevUKStudyVisa"
        label="UK Study Visa"
        fullLabel="Did you receive a student visa for your previous UK study?"
        value={hasPreviousUkStudyVisa}
        isRisk={showRisks && riskList.includes(Q.HAS_PREVIOUS_UK_STUDY_VISA)}
      />

      {hasPreviousUkStudy === 'Yes' && hasPreviousUkStudyVisa === 'Yes' && previousUkStudyVisas?.length && (
        <>
          <SummaryRow
            key="prevUKStudyVisaNumber"
            label="Number of Visas"
            fullLabel="How many UK student visas have you received?"
            value={previousUkStudyVisas.length}
          />

          {previousUkStudyVisas && (
            <Box as="ol" pl={8}>
              {previousUkStudyVisas.map((visa: IStudentVisa, index: number) => {
                return (
                  <SummaryGroup key={`study-visa-${index}`}>
                    <SummaryRow
                      label="Type"
                      value={getVisaLabelFromValue(visa.visaType)}
                      fullLabel="What type of student visa? "
                    />
                    <SummaryRow label="Year" value={visa.year} fullLabel="What was the year you received it?" />
                    <SummaryRow label="Level" value={visa.level} />
                    <SummaryRow label="Completed" value={visa.isComplete ? 'Yes' : 'No'} />
                    {visa.isComplete === false && (
                      <SummaryRow label="Incomplete Reason" value={visa.incompleteReason} />
                    )}
                    {visa.isComplete === false && (
                      <SummaryRow
                        label="Is Incomplete Recorded"
                        value={visa.isIncompleteRecorded === true ? 'Yes' : 'No'}
                      />
                    )}
                    <SummaryRow label="Is Visa Shortened" value={visa.isVisaShortened ? 'Yes' : 'No'} />
                    {visa.isVisaShortened && <SummaryRow label="Visa Shortened To" value={visa.shortenedDate} />}
                  </SummaryGroup>
                );
              })}
            </Box>
          )}

          {lastCourseLevel && (
            <SummaryRow
              key="lastCourseLevel"
              label="Last Course Level"
              fullLabel="Are you applying for a course that is at a higher, same or lower-level than the last course you studied in the UK?'"
              value={getCourseLevel(lastCourseLevel)}
              isRisk={showRisks && riskList.includes(Q.LAST_COURSE_LEVEL)}
            />
          )}

          {[CourseLevel.SAME, CourseLevel.LOWER].includes(lastCourseLevel!) && (
            <SummaryRow
              key="lowerOrSameLevelReason"
              label="Lower/Same Level Course Reason"
              fullLabel="Why are you applying for a course at the same or lower level as your previous course?"
              value={lowerOrSameLevelReason}
              isRisk={false}
            />
          )}
        </>
      )}
    </>
  );
}

export default PriorStudyAndTravel;
